import fetchCommerceToolsGraphQLApi from "../fetchCommerceToolsGraphQLApi"
import getIncludeNamesFilter from "../getIncludeNamesFilter"
import formatLockingSystemProduct from "@secureo/common/utils/commerceTools/formatters/formatLockingSystemProduct"
import fetchInitialCylinderConfigurations from "./fetchInitialCylinderConfigurations"
import getGroupsWithRequiredConfiguration from "@secureo/common/reducers/productDetailPageReducer/utils/getGroupsWithRequiredConfiguration"
import fetchConfigurationGroupProducts from "./fetchConfigurationGroupProducts"
import getProductConfigurationFromConfigurationProduct from "@secureo/common/reducers/productDetailPageReducer/utils/configurationGroups/getProductConfigurationFromConfigurationProduct"
import getLanguageAndCountryCodeFromLocale from "@secureo/common/utils/i18n/getLanguageAndCountryCodeFromLocale"

import { RawProduct } from "./fetchProduct"
import { LockingSystemProductsBySku } from "@secureo/common/typings/LockingSystem"
import { ProductConfigurationsByConfigurationGroup } from "@secureo/common/reducers/productDetailPageReducer"
import { CurrencyCode } from "@secureo/common/typings/Forex"

// Only fetched for master variant
export const masterVariantAttributesRawIncludeNames = [
	// General
	"manufacturer",
	"variantsHaveUniqueProductPage",

	// Shipping & delivery
	"shippingOriginationCountryCode",

	// Cylinders
	"configurationLengthCombinations",
	"systemConfigurationToolTip",

	// Locking system
	"lockingSystemComponentType",
	"lockingSystemCharges",
	"lockingSystemComponentName",
]

// Fetched for both masterVariant and variants
export const variantAttributesRawIncludeNames = [
	"shortDescriptionDigishop",
	"msrpPrice",
	"deliveryTimeWorkdays",
	"configurationsGroups",
	"variantName",
]

const query = /* GraphQL */ `
	fragment Variant on ProductVariant {
		id
		sku
		prices {
			country
			channel {
				key
			}
			value {
				centAmount
				currencyCode
			}
		}
		images {
			url
		}
	}

	query lockingSystemProducts($skus: [String!]!) {
		products(skus: $skus, limit: 100) {
			results {
				id
				productType {
					name
				}
				masterData {
					current {
						slug(locale: "de")
						nameDE: name(locale: "de")
						nameAT: name(locale: "de-AT")
						nameCH: name(locale: "de-CH")
						masterVariant {
							...Variant
							attributesRaw(includeNames: [${getIncludeNamesFilter([
								...masterVariantAttributesRawIncludeNames,
								...variantAttributesRawIncludeNames,
							])}]) {
								name
								value
							}
						}
						variants {
							...Variant
							attributesRaw(includeNames: [${getIncludeNamesFilter(variantAttributesRawIncludeNames)}]) {
								name
								value
							}
						}
					}
				}
			}
		}
	}
`

const fetchLockingSystemProductsBySku = async (
	accessToken: string,
	skus: string[],
	locale: string,
	currencyCode: CurrencyCode,
) => {
	const data = (await fetchCommerceToolsGraphQLApi(accessToken, query, { skus })) as {
		products: {
			results: RawProduct[]
		}
	}

	const products = data?.products?.results ?? []
	const formattedProducts = products.map((product) => formatLockingSystemProduct(product, locale))

	const { countryCode } = getLanguageAndCountryCodeFromLocale(locale)

	for (const formattedProduct of formattedProducts) {
		if (formattedProduct.productType === "Zylinder") {
			const masterVariant = formattedProduct?.variants?.[0]

			const { initialCylinderConfigurationVariant, cylinderLockingMechanismProducts } =
				await fetchInitialCylinderConfigurations(
					accessToken,
					masterVariant.sku,
					masterVariant.attributes,
					locale,
				)

			const groupsWithRequiredConfiguration = getGroupsWithRequiredConfiguration(
				initialCylinderConfigurationVariant?.masterVariant?.attributes
					?.configurationGroups ?? [],
				true,
			)

			const { requiredConfigurationSkus, configurationGroupNamesBySku } =
				groupsWithRequiredConfiguration.reduce(
					(
						{ requiredConfigurationSkus, configurationGroupNamesBySku },
						configurationGroup,
					) => {
						const { items, name } = configurationGroup

						const sku = items?.[0]?.sku ?? null

						if (!sku) return { requiredConfigurationSkus, configurationGroupNamesBySku }

						return {
							requiredConfigurationSkus: [...requiredConfigurationSkus, sku],
							configurationGroupNamesBySku: {
								...configurationGroupNamesBySku,
								[sku]: name,
							},
						}
					},
					{ requiredConfigurationSkus: [], configurationGroupNamesBySku: {} } as {
						requiredConfigurationSkus: string[]
						configurationGroupNamesBySku: {
							[sku: string]: string
						}
					},
				)

			const requiredConfigurationGroupProducts = await fetchConfigurationGroupProducts(
				accessToken,
				requiredConfigurationSkus,
				currencyCode,
			)

			const initialConfigurationsByConfigurationGroup =
				requiredConfigurationGroupProducts.reduce(
					(initialConfigurationsByConfigurationGroup, configurationProduct) => {
						const { sku } = configurationProduct

						const configurationGroupName = configurationGroupNamesBySku?.[sku] ?? null

						const configuration = getProductConfigurationFromConfigurationProduct(
							configurationProduct,
							configurationGroupName,
							1,
							countryCode,
						)

						return {
							...initialConfigurationsByConfigurationGroup,
							[configurationGroupName]: {
								[sku]: configuration,
							},
						}
					},
					{} as ProductConfigurationsByConfigurationGroup,
				)

			if (initialCylinderConfigurationVariant)
				formattedProduct.initialCylinderConfigurationVariant =
					initialCylinderConfigurationVariant
			if (cylinderLockingMechanismProducts)
				formattedProduct.cylinderLockingMechanismProducts = cylinderLockingMechanismProducts

			if (initialConfigurationsByConfigurationGroup)
				formattedProduct.initialConfigurationsByConfigurationGroup =
					initialConfigurationsByConfigurationGroup
		}
	}

	const lockingSystemProductsBySku = formattedProducts.reduce(
		(lockingSystemProductsBySku, product) => {
			const { sku } = product
			return { ...lockingSystemProductsBySku, [sku]: product }
		},
		{} as LockingSystemProductsBySku,
	)

	return lockingSystemProductsBySku
}

export default fetchLockingSystemProductsBySku
